// src/contexts/NotificationContext.tsx
"use client";

import React, { createContext, useContext, useState, useCallback } from "react";
import { useSession } from "next-auth/react";

export type NotificationType = "success" | "error" | "info" | "warning";

export interface Notification {
	id: string;
	type: NotificationType;
	message: string;
	title?: string;
	isAdminOnly?: boolean;
	createdAt: Date;
	source?: string;
	sourceId?: string;
	link?: string;
}

interface NotificationContextType {
	notifications: Notification[];
	addNotification: (
		notification: Omit<Notification, "id" | "createdAt">
	) => void;
	removeNotification: (id: string) => void;
	clearNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
	undefined
);

export function NotificationProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	const [notifications, setNotifications] = useState<Notification[]>([]);
	const { data: session } = useSession();
	const isAdmin = session?.user?.role === "ADMIN";

	const addNotification = useCallback(
		(notification: Omit<Notification, "id" | "createdAt">) => {
			// Admin olmayan kullanıcılar için admin bildirimlerini gösterme
			if (notification.isAdminOnly && !isAdmin) return;

			const newNotification: Notification = {
				...notification,
				id: Math.random().toString(36).substr(2, 9),
				createdAt: new Date(),
			};

			setNotifications((prev) => [...prev, newNotification]);

			// 5 saniye sonra bildirimi otomatik kaldır
			setTimeout(() => {
				removeNotification(newNotification.id);
			}, 5000);
		},
		[isAdmin]
	);

	const removeNotification = useCallback((id: string) => {
		setNotifications((prev) =>
			prev.filter((notification) => notification.id !== id)
		);
	}, []);

	const clearNotifications = useCallback(() => {
		setNotifications([]);
	}, []);

	return (
		<NotificationContext.Provider
			value={{
				notifications,
				addNotification,
				removeNotification,
				clearNotifications,
			}}
		>
			{children}
		</NotificationContext.Provider>
	);
}

export function useNotification() {
	const context = useContext(NotificationContext);
	if (context === undefined) {
		throw new Error(
			"useNotification must be used within a NotificationProvider"
		);
	}
	return context;
}
